import React from "react";
import type { SidebarFormProps } from "./SidebarForm";
import { ImageUpload, ImageUploadMetadata, Input, InputWithIcon, ToggleSwitch } from "../../../../components";
import { useTranslations } from "../../../../contexts";
import { detectSocialPlatform, getSocialIcon } from "../../../../helpers/socialLinks";
import { isEmpty } from "lodash";
import { stripQuery, withSiteId } from "../../../../helpers/imageUri";
import { normalizeHttpUrl } from "../../../../helpers/externalUrl";
import { DesignerSectionHeading } from "./DesignerSectionHeading";

export type TeamMember = NonNullable<
  NonNullable<SidebarFormProps["form"]["teams"]>["items"]
>[number];

export function updateTeamMemberItems({
  items,
  index,
  updates,
  createId,
}: {
  items: TeamMember[];
  index: number;
  updates: Partial<
    TeamMember & { social_links?: { id?: string; url: string }[] }
  >;
  createId: () => string;
}): TeamMember[] {
  const member = items[index];
  if (!member) {
    const newMember: TeamMember = { id: createId(), ...updates };
    return [...items, newMember];
  }

  const updatedMember: TeamMember = { ...member, ...updates };
  const hasNoName = isEmpty(updatedMember.name);
  const hasNoPhoto = isEmpty(updatedMember.photo_uri);
  const hasNoSocialLinks = !updatedMember.social_links?.some(
    (link) => !isEmpty(link.url)
  );

  if (hasNoName && hasNoPhoto && hasNoSocialLinks) {
    return items.filter((_, i) => i !== index);
  }

  return items
    .slice(0, index)
    .concat(updatedMember)
    .concat(items.slice(index + 1));
}

export const SidebarFormTeams: React.FC<SidebarFormProps> = (props) => {
  const i18n = useTranslations();
  const { siteId } = props;

  const setImageMetadata = (uri: string, metadata?: ImageUploadMetadata) => {
    if (!metadata) return;
    const cleanedUri = stripQuery(uri);
    props.setImageMetadata?.({
      ...props.imageMetadata,
      [cleanedUri]: metadata,
    });
  };

  const updateMember = (
    index: number,
    updates: Partial<TeamMember & { social_links?: { id?: string; url: string }[] }>
  ) => {
    const items = props.form.teams?.items || [];
    const nextItems = updateTeamMemberItems({
      items,
      index,
      updates,
      createId: () => crypto.randomUUID(),
    });
    props.setForm({
      ...props.form,
      teams: {
        ...props.form.teams,
        is_enabled: props.form.teams?.is_enabled ?? true,
        items: nextItems,
      },
    });
  };

  const updateSocialLink = (
    memberIndex: number,
    linkIndex: number,
    url: string
  ) => {
    const member = props.form.teams?.items?.[memberIndex];
    const existingLinks = member?.social_links || [];

    const linkAtIndex = existingLinks[linkIndex];

    if (isEmpty(url)) {
      if (linkAtIndex) {
        const newLinks = existingLinks.filter((_, i) => i !== linkIndex);
        updateMember(memberIndex, { social_links: newLinks });
      }
      return;
    }

    if (linkAtIndex) {
      const newLinks = existingLinks
        .slice(0, linkIndex)
        .concat({ ...linkAtIndex, url })
        .concat(existingLinks.slice(linkIndex + 1));
      updateMember(memberIndex, { social_links: newLinks });
    } else {
      const newLinks = [...existingLinks, { id: crypto.randomUUID(), url }];
      updateMember(memberIndex, { social_links: newLinks });
    }
  };

  return (
    <div
      id="teams-form"
      className="border-b p-6"
      data-preview-section="about"
    >
      <div className="flex items-start">
        <DesignerSectionHeading
          className="grow pb-4"
          title={i18n.Team}
          helpLabel={`${i18n.designerHelpFor} ${i18n.Team}`}
          helpText={i18n.designerTeamHelp}
        />
        <ToggleSwitch
          aria-label={`${i18n.designerShowSection}: ${i18n.Team}`}
          checked={props.form.teams?.is_enabled ?? false}
          onChange={() =>
            props.setForm({
              ...props.form,
              teams: {
                ...props.form.teams,
                is_enabled: !props.form.teams?.is_enabled,
              },
            })
          }
        />
      </div>
      {props.form.teams?.is_enabled && (
        <div>
          <div className="pb-5 mb-5 border-b border-gray-300">
            <div className="mb-3">
              <Input
                variant="outlined"
                placeholder={i18n["title"]}
                example={i18n.designerTeamTitlePlaceholder}
                help={{
                  label: `${i18n.designerHelpFor} ${i18n.title}`,
                  content: i18n.designerTeamTitleHelp,
                }}
                value={props.form.teams.title || ""}
                onChange={(event) => {
                  props.setForm({
                    ...props.form,
                    teams: {
                      ...props.form.teams,
                      is_enabled: props.form.teams?.is_enabled ?? true,
                      title: event.target.value,
                    },
                  });
                }}
              />
            </div>
            <div>
              <Input
                variant="outlined"
                placeholder={i18n["description"]}
                example={i18n.designerTeamDescriptionPlaceholder}
                asTextarea
                help={{
                  label: `${i18n.designerHelpFor} ${i18n.description}`,
                  content: i18n.designerTeamDescriptionHelp,
                }}
                value={props.form.teams.description || ""}
                onChange={(event) => {
                  props.setForm({
                    ...props.form,
                    teams: {
                      ...props.form.teams,
                      is_enabled: props.form.teams?.is_enabled ?? true,
                      description: event.target.value,
                    },
                  });
                }}
              />
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {[
              ...(props.form.teams?.items || []),
              { id: crypto.randomUUID() },
            ]?.map((member, index) => (
              <div key={member.id} className="py-5 first:pt-0">
                <div className="flex items-center gap-3 mb-2">
                  <ImageUpload
                    variant="avatar"
                    usage="team_member"
                    className="w-12 h-12 flex-shrink-0"
                    image={member.photo_uri ? withSiteId(member.photo_uri, siteId) : member.photo_uri}
                    onChange={({ uri, metadata }) => {
                      setImageMetadata(uri, metadata);
                      updateMember(index, { photo_uri: stripQuery(uri) });
                    }}
                    onDelete={() => updateMember(index, { photo_uri: "" })}
                  />
                  <Input
                    className="flex-1"
                    variant="outlined"
                    placeholder={i18n["name"]}
                    example={i18n.designerTeamMemberNamePlaceholder}
                    help={{
                      label: `${i18n.designerHelpFor} ${i18n.name}`,
                      content: i18n.designerTeamMemberNameHelp,
                    }}
                    value={member.name || ""}
                    onChange={(event) =>
                      updateMember(index, { name: event.target.value })
                    }
                  />
                </div>

                <div className="mt-3">
                  <DesignerSectionHeading
                    level={3}
                    className="mb-2 text-gray-500"
                    title={i18n["Social links"]}
                    helpLabel={`${i18n.designerHelpFor} ${i18n["Social links"]}`}
                    helpText={i18n.designerSocialUrlHelp}
                  />
                  <div className="space-y-2">
                    {[
                      ...(member.social_links || []),
                      { id: crypto.randomUUID(), url: "" },
                    ].map((link, linkIndex) => {
                      const platform = detectSocialPlatform(link.url);
                      const Icon = getSocialIcon(platform);
                      const iconColor = "text-gray-500";

                      return (
                        <InputWithIcon
                          key={link.id}
                          inputProps={{
                            variant: "outlined",
                            placeholder: i18n["Social links"],
                            example: i18n.designerSocialUrlPlaceholder,
                            help: {
                              label: `${i18n.designerHelpFor} ${i18n["Social links"]}`,
                              content: i18n.designerSocialUrlHelp,
                            },
                            value: link.url,
                            onChange: (event) =>
                              updateSocialLink(
                                index,
                                linkIndex,
                                event.target.value
                              ),
                            onBlur: (event) => {
                              const normalizedUrl = normalizeHttpUrl(
                                event.target.value
                              );
                              if (normalizedUrl === link.url) return;
                              updateSocialLink(index, linkIndex, normalizedUrl);
                            },
                          }}
                          icon={<Icon className="w-4 h-4" />}
                          iconColor={iconColor}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
